import type { ThemeColorName } from '@examdojo/styles';
import { JsxParamStore } from '@examdojo/core/widget';
import type { ElementDisplayConfig, FunctionElementDef } from '../../../graphpad.model';
import type { ElementContext, FunctionElement } from '../../element.model';
import { createHandlesFromConfigs } from '../../features/handle';
import { formatCoefficient } from '../expression.utils';
import { BaseFunctionCreator } from '../function.model';
import { CUBIC_GENERAL_META, type CubicGeneralParams } from './cubic-general.model';

type CubicGeneralParamsIndexed = CubicGeneralParams & { [key: string]: number };

export class CubicGeneralCreator extends BaseFunctionCreator<CubicGeneralParamsIndexed> {
  readonly functionType = 'cubic-general';
  readonly defaultParams: CubicGeneralParamsIndexed = { ...CUBIC_GENERAL_META.defaultParams };
  readonly availableFeatures = CUBIC_GENERAL_META.availableFeatures;
  readonly handleConfigs = CUBIC_GENERAL_META.handleConfigs;
  override readonly computedValueKeys = [
    'inflectionX',
    'inflectionY',
    'discriminant',
    'yIntercept',
    'localMinX',
    'localMinY',
    'localMaxX',
    'localMaxY',
  ] as const;

  create(
    id: string,
    def: FunctionElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): FunctionElement {
    const { board, elementFactory, themeService } = context;
    const initialParams = { ...this.defaultParams, ...def.params } as CubicGeneralParamsIndexed;

    const colorName = (display?.curve?.color ?? 'indigo') as ThemeColorName;
    const strokeColor = themeService.getColor(colorName);

    const paramStore = new JsxParamStore(board, {
      a: { value: initialParams.a, range: CUBIC_GENERAL_META.defaultRanges.a },
      b: { value: initialParams.b, range: CUBIC_GENERAL_META.defaultRanges.b },
      c: { value: initialParams.c, range: CUBIC_GENERAL_META.defaultRanges.c },
      d: { value: initialParams.d, range: CUBIC_GENERAL_META.defaultRanges.d },
    });

    const { handles, handlePoints } = createHandlesFromConfigs(
      board,
      paramStore,
      this.handleConfigs,
      initialParams,
      strokeColor,
    );

    const evaluate = (x: number): number => {
      const a = paramStore.getValue('a');
      const b = paramStore.getValue('b');
      const c = paramStore.getValue('c');
      const d = paramStore.getValue('d');
      return a * x * x * x + b * x * x + c * x + d;
    };

    const getExpression = (): string => {
      const a = paramStore.getDisplayValue('a');
      const b = paramStore.getDisplayValue('b');
      const c = paramStore.getDisplayValue('c');
      const d = paramStore.getDisplayValue('d');
      return buildCubicGeneralExpression(a, b, c, d);
    };

    const curveResult = elementFactory.curve({
      type: 'functiongraph',
      fn: evaluate,
      name: id,
      style: display?.curve,
      getExpression,
    });

    const objects = [...paramStore.getAllSliders(), ...handlePoints, curveResult.curve];
    if (curveResult.label) {
      objects.push(curveResult.label);
    }

    // f'(x) = 3ax² + 2bx + c
    // f''(x) = 6ax + 2b
    // f'''(x) = 6a
    const getDerivative = (order = 1): ((x: number) => number) => {
      if (order === 1) {
        return (x: number) => {
          const a = paramStore.getValue('a');
          const b = paramStore.getValue('b');
          const c = paramStore.getValue('c');
          return 3 * a * x * x + 2 * b * x + c;
        };
      }
      if (order === 2) {
        return (x: number) => {
          const a = paramStore.getValue('a');
          const b = paramStore.getValue('b');
          return 6 * a * x + 2 * b;
        };
      }
      if (order === 3) {
        return () => 6 * paramStore.getValue('a');
      }
      return () => 0;
    };

    return {
      id,
      type: 'cubic-general',
      objects,
      evaluate,
      handleConfigs: this.handleConfigs,
      handles,
      getParams: () => paramStore.getParams() as CubicGeneralParamsIndexed,
      setParams: (p) => paramStore.setParams(p as Record<string, number>),
      setDisplaySteps: (steps) => {
        for (const [name, step] of Object.entries(steps)) {
          paramStore.setDisplayStep(name, step);
        }
      },
      getDerivative,
      getExpression,
      getComputedValues: () => ({
        inflectionX: () => -paramStore.getValue('b') / (3 * paramStore.getValue('a')),
        inflectionY: () => {
          const a = paramStore.getValue('a');
          const b = paramStore.getValue('b');
          const c = paramStore.getValue('c');
          const d = paramStore.getValue('d');
          const h = -b / (3 * a);
          return a * h * h * h + b * h * h + c * h + d;
        },
        discriminant: () => {
          const a = paramStore.getValue('a');
          const b = paramStore.getValue('b');
          const c = paramStore.getValue('c');
          // Discriminant of f'(x) = 3ax² + 2bx + c: (2b)² - 4(3a)(c)
          return 4 * b * b - 12 * a * c;
        },
        yIntercept: () => paramStore.getValue('d'),
        localMinX: () => {
          const a = paramStore.getValue('a');
          const b = paramStore.getValue('b');
          const c = paramStore.getValue('c');
          const disc = b * b - 3 * a * c;
          if (disc < 0) {
            return NaN;
          }
          // f''(x₁) = 2√(b²-3ac) > 0 → always a local minimum
          return (-b + Math.sqrt(disc)) / (3 * a);
        },
        localMinY: () => {
          const a = paramStore.getValue('a');
          const b = paramStore.getValue('b');
          const c = paramStore.getValue('c');
          const d = paramStore.getValue('d');
          const disc = b * b - 3 * a * c;
          if (disc < 0) {
            return NaN;
          }
          const x = (-b + Math.sqrt(disc)) / (3 * a);
          return a * x * x * x + b * x * x + c * x + d;
        },
        localMaxX: () => {
          const a = paramStore.getValue('a');
          const b = paramStore.getValue('b');
          const c = paramStore.getValue('c');
          const disc = b * b - 3 * a * c;
          if (disc < 0) {
            return NaN;
          }
          // f''(x₂) = -2√(b²-3ac) < 0 → always a local maximum
          return (-b - Math.sqrt(disc)) / (3 * a);
        },
        localMaxY: () => {
          const a = paramStore.getValue('a');
          const b = paramStore.getValue('b');
          const c = paramStore.getValue('c');
          const d = paramStore.getValue('d');
          const disc = b * b - 3 * a * c;
          if (disc < 0) {
            return NaN;
          }
          const x = (-b - Math.sqrt(disc)) / (3 * a);
          return a * x * x * x + b * x * x + c * x + d;
        },
      }),
      availableFeatures: this.availableFeatures,
      destroy: () => {
        objects.forEach((obj) => board.removeObject(obj));
      },
    };
  }
}

/**
 * Build a LaTeX string for a cubic in general form: ax³ + bx² + cx + d
 * Simplifies common cases (a=1, a=-1, b=0, c=0, d=0).
 */
function buildCubicGeneralExpression(a: number, b: number, c: number, d: number): string {
  const fmt = formatCoefficient;
  const parts: string[] = [];

  const isAOne = Math.abs(a - 1) < 1e-9;
  const isANegOne = Math.abs(a + 1) < 1e-9;
  const isBZero = Math.abs(b) < 1e-9;
  const isCZero = Math.abs(c) < 1e-9;
  const isDZero = Math.abs(d) < 1e-9;

  // ax³ term
  if (isAOne) {
    parts.push('x^3');
  } else if (isANegOne) {
    parts.push('-x^3');
  } else {
    parts.push(`${fmt(a)}x^3`);
  }

  // bx² term
  if (!isBZero) {
    if (Math.abs(b - 1) < 1e-9) {
      parts.push('+ x^2');
    } else if (Math.abs(b + 1) < 1e-9) {
      parts.push('- x^2');
    } else if (b > 0) {
      parts.push(`+ ${fmt(b)}x^2`);
    } else {
      parts.push(`- ${fmt(-b)}x^2`);
    }
  }

  // cx term
  if (!isCZero) {
    if (Math.abs(c - 1) < 1e-9) {
      parts.push('+ x');
    } else if (Math.abs(c + 1) < 1e-9) {
      parts.push('- x');
    } else if (c > 0) {
      parts.push(`+ ${fmt(c)}x`);
    } else {
      parts.push(`- ${fmt(-c)}x`);
    }
  }

  // d term
  if (!isDZero) {
    if (d > 0) {
      parts.push(`+ ${fmt(d)}`);
    } else {
      parts.push(`- ${fmt(-d)}`);
    }
  }

  return `$${parts.join(' ')}$`;
}
